import { useAllMailbox } from "http-hooks/mailbox/useMailbox";
import ViewMailModal from "components/mailbox/ViewMailModal";
import { useState } from "react";
import Modal from "components/ui/modals";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
  ActionButton,
} from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import Input from "components/ui/Input";
import Pagination from "components/ui/Pagination";
import { EyeIcon, AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import { formatDateAndTime } from "utils/date";

const initialFilter = {
  receiver: "",
  fromDate: "",
  toDate: "",
  page: 1,
  limit: 20,
};

export default function MailTracker() {
  const [viewMailId, setViewMailId] = useState(null);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState(initialFilter);

  const { data, isLoading } = useAllMailbox(filter);
  const mails = data?.data || [];
  const currentPage = data?.page || 1;
  const totalPages = data?.totalPages || 1;

  const handleSearch = (searchValue) => {
    setFilter((prev) => ({
      ...prev,
      receiver: searchValue,
      page: 1,
    }));
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleDateFilterChange = (field, value) => {
    setFilter((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };


  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              Mail Tracker
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Track all emails sent from the system
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6">
          <div className="flex items-start gap-3">
            <div className="flex-1 max-w-md">
              <InputSearch
                onSearch={handleSearch}
                onChange={handleSearchChange}
                placeholder="Search by email address"
                label="Search"
                value={searchText}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowDateFilters(!showDateFilters)}
              className="mt-8 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              title="Additional filters"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
            </button>
          </div>

          {showDateFilters && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Input
                type="date"
                id="fromDate"
                name="fromDate"
                label="From Date"
                value={filter.fromDate}
                onChange={(e) =>
                  handleDateFilterChange("fromDate", e.target.value)
                }
              />
              <Input
                type="date"
                id="toDate"
                name="toDate"
                label="To Date"
                value={filter.toDate}
                onChange={(e) =>
                  handleDateFilterChange("toDate", e.target.value)
                }
              />
            </div>
          )}
        </div>

        {/* Table */}
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <Table>
                <THead>
                  <TR>
                    <TH first>Company</TH>
                    <TH>Subject</TH>
                    <TH>Sent Time</TH>
                    <TH last>Action</TH>
                  </TR>
                </THead>
                <TBody>
                  {isLoading ? (
                    <LoadingRow colSpan={4} />
                  ) : mails.length === 0 ? (
                    <EmptyRow colSpan={4} message="No mails found" />
                  ) : (
                    mails.map((mail) => (
                      <TR key={mail.id}>
                        <TD first>
                          <div className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">
                            {mail.receiverCompanies &&
                            mail.receiverCompanies.length > 0
                              ? mail.receiverCompanies
                                  .map((c) => c.name)
                                  .join(", ")
                              : "-"}
                          </div>
                        </TD>
                        <TD>
                          <div className="max-w-md overflow-hidden text-ellipsis whitespace-nowrap font-semibold">
                            {mail.subject}
                          </div>
                        </TD>
                        <TD>
                          <div className="text-sm text-gray-600">
                            {formatDateAndTime(mail.senttime, "MMM DD, YYYY hh:mm A")}
                          </div>
                        </TD>
                        <TD last>
                          <ActionButton
                            onClick={() => setViewMailId(mail.id)}
                            variant="primary"
                            title="View Details"
                            className="mr-0"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </ActionButton>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {!isLoading && mails.length > 0 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              showPageJump={true}
            />
          </div>
        )}
      </div>

      {/* View Mail Modal */}
      {viewMailId && (
        <Modal
          open={!!viewMailId}
          setOpen={(val) => !val && setViewMailId(null)}
          title="Mail Details"
        >
          <ViewMailModal
            mailId={viewMailId}
            onClose={() => setViewMailId(null)}
          />
        </Modal>
      )}
    </>
  );
}

